import { data_from_server_post } from '@/shared/http/client';
import {
    NATIVE_CLASS_PROBS_PROVIDERS,
    ComparisonModeOption,
    TaskType,
    getTaskTypeLabel,
    isGenerativeTask,
} from '@/shared/domain/taskTypes';
import { normalizeForApi } from '@/config/runtimeStrategyOptions';
import { DATASET_SIZE_LIMIT_LABEL } from '@/shared/constants/datasetSizeLimits';

const PROVIDER_LABELS = {
    openai: 'OpenAI',
    anthropic: 'Anthropic',
    google: 'Google',
    deepseek: 'DeepSeek',
    jev: 'TypeSafe AI',
    bedrock: 'Bedrock',
    vertex: 'VertexAI',
    azure: 'AzureAI',
};

export const providerDisplayLabel = (provider) => PROVIDER_LABELS[provider] || provider;

/**
 * When teacher comparison is on, verify the saved teacher model's provider is
 * still configured (its credentials may have been deleted in Settings since
 * the model was selected). ``availableProviders`` is the map returned by
 * ``useCommercialModels()`` (``GET /available-models``), which only contains
 * providers with registered credentials. Skips the check while the catalog is
 * still loading (``availableProviders`` not yet an object) to avoid false
 * blocks on first paint.
 * @returns {Object|null} A validation failure object, or null when OK.
 */
export const validateTeacherProviderConfigured = (usecase, providerCatalog) => {
    const { availableProviders, isLoading } = providerCatalog || {};
    if (isLoading || !availableProviders || typeof availableProviders !== 'object') {
        return null;
    }
    // Mirrors the backend default (teacher_provider falls back to openai).
    const teacherProvider = usecase.teacher_provider || 'openai';
    if (availableProviders[teacherProvider]) {
        return null;
    }
    const providerLabel = providerDisplayLabel(teacherProvider);
    return {
        success: false,
        reason: 'teacher_provider_not_configured',
        provider: teacherProvider,
        providerLabel,
        message: `The selected commercial model '${usecase.teacher_model_display || usecase.teacher_model}' uses the ${providerLabel} provider, but no ${providerLabel} credentials are configured. Add credentials in Settings > Model Providers or select a different commercial model in the 'Model Configuration' step.`,
    };
};

/**
 * Generative tasks (summarization / content generation) always run an
 * LLM-as-a-judge evaluation against the usecase's model provider
 * (``teacher_provider``, which mirrors the backend default of openai even
 * when no commercial model was ever selected). When that provider has no
 * configured credentials the backend rejects ``create_distillation_event``
 * with a 400 — block up-front with an indicative modal instead.
 * @returns {Object|null} A validation failure object, or null when OK.
 */
export const validateJudgeProviderConfigured = (usecase, providerCatalog) => {
    if (!isGenerativeTask(usecase?.task_type)) {
        return null;
    }
    const { availableProviders, isLoading } = providerCatalog || {};
    if (isLoading || !availableProviders || typeof availableProviders !== 'object') {
        return null;
    }
    const judgeProvider = usecase.teacher_provider || 'openai';
    // Native class-probs providers (TypeSafe AI's Jev) answer classification
    // questions only — they can never serve as the LLM-as-a-judge, so having
    // their credentials configured does not unblock generative training.
    const judgeCapable = !NATIVE_CLASS_PROBS_PROVIDERS.includes(judgeProvider);
    if (judgeCapable && availableProviders[judgeProvider]) {
        return null;
    }
    const taskTypeLabel = getTaskTypeLabel(usecase.task_type);
    // With comparison off there is no commercial-model selection, so pointing
    // the user at the Model Configuration step would be confusing — only the
    // Settings (credentials) advice applies.
    const showModelConfigurationHint = Boolean(usecase.comparison_mode);
    return {
        success: false,
        reason: 'judge_provider_not_configured',
        provider: judgeProvider,
        providerLabel: providerDisplayLabel(judgeProvider),
        taskTypeLabel,
        showModelConfigurationHint,
        message: showModelConfigurationHint
            ? `The current model provider isn’t supported for ${taskTypeLabel} training. Add credentials in Settings or select a different commercial model in the Model Configuration step`
            : `The current model provider isn’t supported for ${taskTypeLabel} training. Add credentials in Settings.`,
    };
};

/**
 * Validates if training can be started
 * @param {Object} usecase - The current usecase
 * @param {Object} [providerCatalog] - ``{ availableProviders, isLoading }`` from ``useCommercialModels()``
 * @param {Object|null} [trainingDataSize] - ``training_data_size`` from the
 *   ``datasets-and-benchmarks-meta-data`` response
 *   (``{ total_bytes, limit_bytes, exceeds_limit, message }``). Absent/null
 *   means the backend reported no size info and never blocks.
 * @returns {Object} Validation result with success and error message
 */
export const validateTrainingStart = (
    usecase,
    datasetMetadata,
    benchmarkMetadata,
    benchamarks,
    providerCatalog,
    trainingDataSize = null,
) => {
    if (!usecase?.datasets?.[0]) {
        return {
            success: false,
            reason: 'missing_training_data',
            message: "Go to 'Data Selection' Tab and choose your data for training"
        };
    }
    if (trainingDataSize?.exceeds_limit) {
        return {
            success: false,
            reason: 'training_data_too_large',
            message:
                trainingDataSize.message ||
                `The selected training data exceeds the ${DATASET_SIZE_LIMIT_LABEL} limit for training. Remove or replace some datasets or benchmarks and try again.`,
        };
    }
    if (!usecase.prompt) {
        return {
            success: false,
            reason: 'missing_prompt',
            message: "You must go to 'Model Configuration' Step and fill the 'Prompt' field"
        };
    }
    const isTeacherRequired = usecase.comparison_mode && ['teacher', 'all'].includes(usecase.comparison_mode_option);
    if (isTeacherRequired && !usecase.teacher_model) {
        return {
            success: false,
            reason: 'missing_teacher_model',
            message: "You must go to 'Model Configuration' Step and fill the 'Commercial Model' field"
        };
    }
    if (isTeacherRequired && usecase.teacher_model) {
        const providerFailure = validateTeacherProviderConfigured(usecase, providerCatalog);
        if (providerFailure) {
            return providerFailure;
        }
    }
    const judgeFailure = validateJudgeProviderConfigured(usecase, providerCatalog);
    if (judgeFailure) {
        return judgeFailure;
    }
    // ``num_characters`` stays null until example-summary finishes, while
    // ``num_records`` already reflects active Data Selection rows. Only treat
    // missing characters as "no data" when there are also no active records.
    const datasetHasActiveRecords = (datasetMetadata?.num_records || 0) > 0;
    if (datasetMetadata?.num_characters === null && !datasetHasActiveRecords) {
        return {
            success: false,
            message: "You have no data to train on. maybe the upload not completed? or you didn't upload any data?"
        };
    }
    const benchmarkHasActiveRecords = (benchmarkMetadata?.num_records || 0) > 0;
    if (
        benchamarks &&
        benchamarks?.length > 0 &&
        benchmarkMetadata?.num_characters === null &&
        !benchmarkHasActiveRecords
    ) {
        return {
            success: false,
            message: "You have no data to test on. maybe the upload not completed? or you didn't upload any data?"
        };
    }
    if (usecase.comparison_mode && (usecase.comparison_mode_option == 'all' || usecase.comparison_mode_option == 'teacher') && !usecase.parser_function) {
        return {
            success: false,
            reason: 'missing_parser_function',
            message: "You must go to 'Model Configuration' Step and fill the 'Response Parser' function"
        };
    }

    return { success: true };
};

/**
 * Whether the student prompts are included in training & evaluation samples.
 *
 * Configured on Model Configuration > SpecificAI model: each prompt field is
 * ``null`` (Same as commercial), ``""`` (Disable) or text (Customize). The
 * flag is off only when BOTH prompts are disabled. Tasks that were never
 * configured (both ``null``) keep the historical per-task default:
 * content generation on, summarization off.
 */
export const deriveIncludeInstructionInTraining = (usecase) => {
    if (!isGenerativeTask(usecase?.task_type)) return false;
    const systemPrompt = usecase?.generative_student_instructions ?? null;
    const userPrompt = usecase?.generative_student_prompt ?? null;
    if (systemPrompt == null && userPrompt == null) {
        return usecase?.task_type === TaskType.contentGeneration;
    }
    const disabled = (value) => value != null && String(value).trim() === '';
    return !(disabled(systemPrompt) && disabled(userPrompt));
};

const isEmptyPromptMode = (value) => value != null && String(value).trim() === '';

/**
 * Model Setup "Compare to a commercial model" is on, and the option includes
 * a teacher (not "none", and not external results only).
 */
export const isTeacherComparisonEnabled = (usecase) => {
    if (!usecase?.comparison_mode) return false;
    const option = usecase.comparison_mode_option;
    if (option === ComparisonModeOption.none) return false;
    if (option === ComparisonModeOption.manual_model_predictions) return false;
    return true;
};

/**
 * Summarization / content generation show "When evaluating" only when the
 * student system or user prompt is included in training and evaluation and
 * at least one of those prompts is not the Empty override.
 *
 * ``includeInstructionInTraining === undefined`` uses
 * ``deriveIncludeInstructionInTraining``. An explicit boolean is the
 * Evaluate New Version checkbox for this run.
 */
export const generativeEvaluationNormalizationVisible = (usecase, includeInstructionInTraining) => {
    if (!isGenerativeTask(usecase?.task_type)) return false;
    const include = includeInstructionInTraining == null
        ? deriveIncludeInstructionInTraining(usecase)
        : Boolean(includeInstructionInTraining);
    if (!include) return false;
    const systemPrompt = usecase?.generative_student_instructions;
    const userPrompt = usecase?.generative_student_prompt;
    if (isEmptyPromptMode(systemPrompt) && isEmptyPromptMode(userPrompt)) return false;
    return true;
};

/** One "When evaluating" block: teacher comparison, or generative prompts in use. */
export const shouldShowEvaluationNormalization = (usecase, includeInstructionInTraining) => (
    isTeacherComparisonEnabled(usecase)
    || generativeEvaluationNormalizationVisible(usecase, includeInstructionInTraining)
);

/**
 * `model_evaluation_arguments.rare_class_label` for the Advanced "Optimize
 * on" pick — `{ rare_class_label }` when a label is chosen, `{}` for the
 * default "All labels". Sent identically on `create_distillation_event` and
 * `create_training` (trainer contract: unset means all labels).
 */
const rareClassLabelField = (trainingParams) => {
    const label = trainingParams?.rareClassLabel;
    return typeof label === 'string' && label.length > 0 ? { rare_class_label: label } : {};
};

/**
 * Builds the `create_distillation_event` request body without sending it.
 * Shared by the normal "Let's Train" flow and the auto-train arm snapshot.
 * @returns {{ payload: Object, newVersion: number }}
 */
export const buildDistillationEventPayload = (usecase, trainingParams, extraFields = {}) => {
    const newVersion = (usecase?.version || 0) + 1;
    const modelArguments = {
        optimization_metric: trainingParams.metricOptimization,
        f_score_metric_beta_coefficient: trainingParams.betaForFScore || 1,
        // "Optimize on": compute the metric for this label only. Omitted
        // (never null) when the default "All labels" is kept, so older
        // trainers see exactly the payload they did before.
        ...rareClassLabelField(trainingParams),
    };

    const runtimeStrategy = normalizeForApi({
        prediction_strategy: trainingParams.predictionStrategy,
    });

    // Generative tasks include the usecase-level prompts in every
    // training/evaluation sample unless disabled on Model Configuration >
    // SpecificAI model. Student overrides are tri-state: ``null`` inherits
    // from the teacher fields, ``""`` means "Disable" (nothing is sent), any
    // other text is an explicit override. The backend resolves inheritance at
    // training/evaluation time.
    const generativeFields = isGenerativeTask(usecase?.task_type)
        ? {
            include_instruction_in_training: deriveIncludeInstructionInTraining(usecase),
            generative_schema: usecase?.generative_schema != null
                ? usecase.generative_schema
                : null,
            generative_student_prompt: usecase?.generative_student_prompt ?? null,
        }
        : {};

    // "When evaluating" cleanup flags are snapshotted for every task type so
    // the evaluation compares this version with the settings that were active
    // when it trained.
    const evaluationNormalizationFields = {
        evaluation_normalize_casing: Boolean(usecase?.evaluation_normalize_casing),
        evaluation_strip_whitespace: Boolean(usecase?.evaluation_strip_whitespace),
        evaluation_fix_json_format: Boolean(usecase?.evaluation_fix_json_format),
    };

    const payload = {
        ...generativeFields,
        ...evaluationNormalizationFields,
        client_id: usecase?.client_id,
        llm_usecase_id: usecase?._id,
        datasets: usecase?.datasets,
        benchmarks: usecase?.benchmarks,
        teacher_model: usecase?.teacher_model,
        task_type: usecase?.task_type,
        // Train -> Advanced base model. Omitted on Automatic: the backend fills
        // in the UNDECIDED_* placeholder and the training handler picks the model.
        ...(trainingParams.baseModel ? { base_distilled_model: trainingParams.baseModel } : {}),
        instruction: usecase?.prompt,
        // Snapshot the usecase-level prompts and schema for every task
        // type so Model Details can show the "System Prompt Used" and
        // handlers resolve student values against teacher fallbacks.
        // Whether prompts are injected into generative training samples
        // is governed separately by ``include_instruction_in_training``.
        teacher_system_prompt: usecase?.teacher_system_prompt || null,
        teacher_schema: usecase?.teacher_schema || null,
        generative_student_instructions: usecase?.generative_student_instructions ?? null,
        version: newVersion,
        parser_function: usecase?.parser_function,
        hardware_device: trainingParams.trainingDevice || 'gpu',
        comparison_mode: usecase?.comparison_mode_option !== 'none' && usecase?.comparison_mode,
        comparison_mode_option: usecase?.comparison_mode_option,
        is_multilabel: usecase?.is_multilabel,
        classify_speakers: usecase?.classify_speakers,
        model_evaluation_arguments: modelArguments,
        teacher_provider: usecase?.teacher_provider,
        key_changes: usecase?.key_changes || [],
        task_languages: usecase?.task_languages || ["en"],
        usecase_goal: usecase?.usecase_goal,
        prediction_strategy: runtimeStrategy.prediction_strategy,
        ...extraFields,
    };

    return { payload, newVersion };
};

/**
 * Builds the `create_training` request body without sending it.
 * Shared by the normal "Let's Train" flow and the auto-train arm snapshot.
 */
export const buildTrainingPayload = (usecase, distillationEventId, newVersion, trainingParams) => {
    // Build payload and add beta only when F-beta is the selected metric
    const payload = {
        client_id: usecase?.client_id,
        version: newVersion,
        llm_usecase_id: usecase?._id,
        task_type: usecase?.task_type,
        ...(trainingParams.baseModel ? { base_model_name: trainingParams.baseModel } : {}),
        distillation_event_id: distillationEventId,
        training_params: {
            train_ratio: trainingParams.train,
            validation_ratio: trainingParams.validation,
            test_ratio: 100 - trainingParams.train - trainingParams.validation,
            args: {
                metric_for_best_model: trainingParams.metricOptimization,
            }
        },
        hardware_device: trainingParams.trainingDevice || 'gpu',
    };

    if (trainingParams.metricOptimization === 'f_beta_score' && typeof trainingParams.betaForFScore === 'number') {
        payload.model_evaluation_arguments = {
            optimization_metric: trainingParams.metricOptimization,
            f_score_metric_beta_coefficient: trainingParams.betaForFScore,
            ...rareClassLabelField(trainingParams),
        }
    } else {
        payload.model_evaluation_arguments = {
            optimization_metric: trainingParams.metricOptimization,
            ...rareClassLabelField(trainingParams),
        }
    }

    return payload;
};

/**
 * Creates a distillation event
 * @param {Object} usecase - The current usecase
 * @param {Object} trainingParams - Training parameters including hardware device
 * @param {Function} onSuccess - Success callback
 * @param {Function} onError - Error callback
 */
export const createDistillationEvent = async (usecase, trainingParams, onSuccess, onError, extraFields = {}) => {
    try {
        const { payload, newVersion } = buildDistillationEventPayload(usecase, trainingParams, extraFields);

        data_from_server_post(
            "create_distillation_event",
            payload,
            async (result) => {
                if (result.distillation_event_id) {
                    onSuccess(result.distillation_event_id, newVersion);
                } else {
                    onError(result.detail || "Failed to create distillation event");
                }
            },
            (errorDetail) => {
                // Ensure callers can end loading state on HTTP errors
                onError(errorDetail || "Failed to create distillation event");
            }
        );
    } catch (error) {
        onError("Failed to create distillation event");
    }
};

/**
 * Creates a training
 * @param {Object} usecase - The current usecase
 * @param {string} distillationEventId - The distillation event ID
 * @param {number} newVersion - The new version number
 * @param {Object} trainingParams - Training parameters
 * @param {Function} onSuccess - Success callback
 * @param {Function} onError - Error callback
 */
export const createTraining = async (
    usecase,
    distillationEventId,
    newVersion,
    trainingParams,
    onSuccess,
    onError
) => {
    try {
        const payload = buildTrainingPayload(usecase, distillationEventId, newVersion, trainingParams);

        data_from_server_post(
            "create_training",
            payload,
            (result) => {
                if (result.success) {
                    onSuccess(distillationEventId);
                } else {
                    onError("Failed to create training");
                }
            },
            (errorDetail) => {
                // Propagate HTTP errors to reset loading state
                onError(errorDetail || "Failed to create training");
            }
        );
    } catch (error) {
        onError("Failed to create training");
    }
};

/**
 * Starts the complete training process
 * @param {Object} usecase - The current usecase
 * @param {Object} trainingParams - Training parameters
 * @param {Function} onDistillationSuccess - Callback when distillation is created
 * @param {Function} onTrainingSuccess - Callback when training is created
 * @param {Function} onError - Error callback
 */
export const startTrainingProcess = async (
    usecase,
    trainingParams,
    onDistillationSuccess,
    onTrainingSuccess,
    onError,
    extraDistillationFields = {},
) => {
    const handleDistillationSuccess = async (distillationEventId, newVersion) => {
        onDistillationSuccess(distillationEventId, newVersion);

        // Create training after distillation event
        await createTraining(
            usecase,
            distillationEventId,
            newVersion,
            trainingParams,
            onTrainingSuccess,
            onError
        );
    };

    await createDistillationEvent(
        usecase,
        trainingParams,
        handleDistillationSuccess,
        onError,
        extraDistillationFields,
    );
};

const fileListKey = (names) => (names || []).join('|');

/**
 * Refresh usecase + Data Overview after training starts. When the backend auto-splits
 * a lone dataset into train/benchmark collections (async via distillation handler),
 * poll until Mongo reflects the new file lists.
 */
export const scheduleUsecaseRefreshAfterTrainingStart = ({
    usecaseId,
    initialDatasets = [],
    initialBenchmarks = [],
    refreshUsecases,
    onDataOverviewRefresh,
    expectAutoSplit = false,
}) => {
    const initialDatasetsKey = fileListKey(initialDatasets);
    const initialBenchmarksKey = fileListKey(initialBenchmarks);

    const hasFileListUpdate = (usecase) => {
        if (!usecase) return false;
        return (
            fileListKey(usecase.datasets) !== initialDatasetsKey
            || fileListKey(usecase.benchmarks) !== initialBenchmarksKey
        );
    };

    const runRefresh = () => {
        refreshUsecases?.((updatedUsecases) => {
            const updated = updatedUsecases?.find((u) => u._id === usecaseId);
            if (hasFileListUpdate(updated)) {
                onDataOverviewRefresh?.();
            }
        });
        onDataOverviewRefresh?.();
    };

    runRefresh();

    if (!expectAutoSplit) {
        return;
    }

    [2000, 5000, 10000, 15000, 20000, 30000].forEach((delayMs) => {
        setTimeout(runRefresh, delayMs);
    });
};

export const getStatusLabel = (status) => {
    return status.split('_').join(' ').toUpperCase();
}